import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notifCenterAPI } from '../utils/api';

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const res = await notifCenterAPI.unreadCount();
      setUnread(res.data.count || 0);
    } catch (e) {
      console.debug('unread-count failed', e);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const res = await notifCenterAPI.list();
      setItems(res.data || []);
    } catch (e) {
      console.debug('notifications list failed', e);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await notifCenterAPI.markRead(n.notification_id);
      } catch (e) {
        console.debug('markRead failed', e);
      }
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) =>
        arr.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x))
      );
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await notifCenterAPI.markAllRead();
    } catch (e) {
      console.debug('markAllRead failed', e);
    }
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-2 text-foreground hover:text-gold transition-colors"
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell size={22} />
        {unread > 0 && (
          <span
            className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-destructive text-white text-[10px] font-semibold"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-background border border-border shadow-lg z-[60]"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-medium uppercase tracking-wide">Notifications</span>
            {items.some((x) => !x.read) && (
              <button
                onClick={markAll}
                className="text-xs text-gold hover:underline flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <Check size={14} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div
                className="px-4 py-10 text-center text-sm text-muted-foreground"
                data-testid="notification-empty"
              >
                No notifications yet
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-accent transition-colors ${
                    !n.read ? 'bg-accent/40' : ''
                  }`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 bg-gold shrink-0" />}
                    <div className={n.read ? 'pl-4' : ''}>
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
